import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory, useParams } from "react-router-dom";

import { message, Spin } from "antd";

import Swal from "sweetalert2";

import LdasApi from "../../../../apis/Ldas.api";
import LdasModal from "../LdasModal";
import { LDAS_DELETE_SWAL_CONFIG, normalizeLdasListPage } from "../LdasUtils";
import useDeleteLdasProduct from "../useDeleteLdasProduct";

import LdasDetailLotTable from "./LdasDetailLotTable";
import LdasDetailProductCard from "./LdasDetailProductCard";
import LdasLotAddModal from "./LdasLotAddModal";

const defaultLotPageSize = 10;

const getLotColumns = ({
  editingLotId,
  inputRefs,
  onNumberChange,
  onNumberBlur,
  onDetail,
  onEdit,
  onDelete,
}) => [
  {
    title: "LOT 넘버",
    dataIndex: "lotNumber",
    key: "lotNumber",
    className: "ldas-detail-lot-number-cell",
    render: (value, record) => {
      const isReadOnly = editingLotId !== record.id;
      return (
        <div className="ldas-detail-lot-number-input-wrap">
          <input
            ref={(el) => {
              inputRefs.current[record.id] = el;
            }}
            type="text"
            className={[
              "ldas-cat-panel__name-input",
              isReadOnly
                ? "ldas-cat-panel__name-input--plain"
                : "ldas-cat-panel__name-input--editing",
            ].join(" ")}
            value={value}
            onChange={(e) => onNumberChange(record.id, e.target.value)}
            onBlur={() => onNumberBlur(record.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter") e.target.blur();
            }}
            readOnly={isReadOnly}
            title={isReadOnly ? (value ?? "") || undefined : undefined}
          />
        </div>
      );
    },
  },
  {
    title: "등록일",
    dataIndex: "registeredAt",
    key: "registeredAt",
    width: 120,
    render: (value, record) => (
      <span className="ldas-cell-date">{value ?? record.createdAt ?? "-"}</span>
    ),
  },
  {
    key: "spacer",
    width: 16,
  },
  {
    title: "관리",
    key: "actions",
    align: "center",
    width: 220,
    render: (_, record) => (
      <div className="ldas-actions-cell">
        <button
          type="button"
          className="ldas-action-btn"
          onClick={() => onDetail(record)}
          disabled={!!record.pending}
        >
          상세
        </button>
        <button
          type="button"
          className="ldas-action-btn"
          onClick={() => onEdit(record.id)}
          disabled={!!record.pending}
        >
          수정
        </button>
        <button
          type="button"
          className="ldas-action-btn ldas-action-btn--danger"
          onClick={() => onDelete(record)}
          disabled={!!record.pending}
        >
          삭제
        </button>
      </div>
    ),
  },
];

const LdasProductDetailComponent = () => {
  const history = useHistory();
  const { productId } = useParams();
  const { deleteLdasProductWithConfirm } = useDeleteLdasProduct();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lotListPage, setLotListPage] = useState({
    content: [],
    pageable: { pageNumber: 0, pageSize: defaultLotPageSize },
    totalElements: 0,
  });
  const [editingLotId, setEditingLotId] = useState(null);
  const [lotAddModalOpen, setLotAddModalOpen] = useState(false);
  const inputRefs = useRef({});
  const lotPageSizeRef = useRef(defaultLotPageSize);
  const loadedProductIdRef = useRef(null);
  const initialLotsSignatureRef = useRef("");

  useEffect(() => {
    lotPageSizeRef.current = lotListPage.pageable.pageSize;
  }, [lotListPage.pageable.pageSize]);

  const refetchLotsOnly = useCallback(
    async (pageNumber, pageSize) => {
      const res = await LdasApi.readLdasLotListByProduct(productId, {
        page: pageNumber,
        size: pageSize,
      });
      console.log("readLdasLotListByProduct res", res);
      if (res?.code === 200) {
        setLotListPage((prev) => {
          const next = normalizeLdasListPage(res, {
            pageNumber,
            pageSize,
          });
          return { ...prev, ...next };
        });
      } else {
        message.error(res?.message ?? "Lot 목록을 불러올 수 없습니다");
      }
    },
    [productId],
  );

  const loadProductAndFirstLotPage = useCallback(async () => {
    if (!productId) return;

    const pageSize = lotPageSizeRef.current;
    loadedProductIdRef.current = null;
    initialLotsSignatureRef.current = "";

    setLotListPage((prev) => ({
      ...prev,
      pageable: { ...prev.pageable, pageNumber: 0 },
    }));

    setLoading(true);
    try {
      const [detailRes, lotRes] = await Promise.all([
        LdasApi.readLdasProductDetail(productId),
        LdasApi.readLdasLotListByProduct(productId, {
          page: 0,
          size: pageSize,
        }),
      ]);

      if (detailRes?.code !== 200) {
        message.error(detailRes?.message ?? "제품을 불러올 수 없습니다");
        history.replace("/ldas/list");
        return;
      }

      setProduct(detailRes?.result);

      if (lotRes?.code === 200) {
        setLotListPage((prev) => ({
          ...prev,
          ...normalizeLdasListPage(lotRes, {
            pageNumber: 0,
            pageSize,
          }),
        }));
      } else {
        message.error(lotRes?.message ?? "Lot 목록을 불러올 수 없습니다");
        setLotListPage((prev) => ({
          ...prev,
          content: [],
          pageable: { ...prev.pageable, pageNumber: 0, pageSize },
          totalElements: 0,
        }));
      }

      loadedProductIdRef.current = productId;
      initialLotsSignatureRef.current = `${productId}\t0\t${pageSize}`;
    } catch (e) {
      console.error("loadProductAndFirstLotPage", e);
      message.error("데이터를 불러올 수 없습니다");
      history.replace("/ldas/list");
    } finally {
      setLoading(false);
    }
  }, [productId, history]);

  useEffect(() => {
    void loadProductAndFirstLotPage();
  }, [loadProductAndFirstLotPage]);

  useEffect(() => {
    if (!productId) return;
    if (loadedProductIdRef.current !== productId) return;

    const { pageNumber, pageSize } = lotListPage.pageable;
    const sig = `${productId}\t${pageNumber}\t${pageSize}`;
    if (sig === initialLotsSignatureRef.current) {
      initialLotsSignatureRef.current = "";
      return;
    }

    void refetchLotsOnly(pageNumber, pageSize);
  }, [
    productId,
    lotListPage.pageable.pageNumber,
    lotListPage.pageable.pageSize,
    refetchLotsOnly,
  ]);

  const handleLotAdd = () => {
    setLotAddModalOpen(true);
  };

  const handleLotAddModalCancel = () => {
    setLotAddModalOpen(false);
  };

  // 로트 추가
  const handleLotAddSubmit = async (trimmedLotName) => {
    const tempId = `__temp_${Date.now()}`;
    const optimisticLot = {
      id: tempId,
      lotNumber: trimmedLotName,
      registeredAt: null,
      createdAt: new Date().toISOString().slice(0, 10),
      pending: true,
    };

    // 1) 모달 닫기 + 낙관적 업데이트
    setLotAddModalOpen(false);
    setLotListPage((prev) => ({
      ...prev,
      content: [optimisticLot, ...prev.content],
      totalElements: prev.totalElements + 1,
    }));

    // 2) API 호출 후 실제 데이터로 교체
    try {
      const res = await LdasApi.createLdasLot({
        productId: Number(productId),
        lotNumber: trimmedLotName,
      });
      if (res?.code === 200) {
        message.success(
          "Lot이 추가되었습니다. 인증 및 테스트 등록을 진행해 주세요.",
        );
        setTimeout(() => {
          history.push(`/ldas/detail/${productId}/lot/${res.result}`);
        }, 300);
      } else {
        if (res?.code !== 200) {
          setLotListPage((prev) => ({
            ...prev,
            content: prev.content.filter((l) => l.id !== tempId),
            totalElements: prev.totalElements - 1,
          }));
          message.error(res?.message ?? "Lot을 추가할 수 없습니다");
          await refetchLotsOnly(
            lotListPage.pageable.pageNumber,
            lotListPage.pageable.pageSize,
          );
          return;
        }
      }
    } catch (e) {
      console.error("createLdasLot", e);
      setLotListPage((prev) => ({
        ...prev,
        content: prev.content.filter((l) => l.id !== tempId),
        totalElements: prev.totalElements - 1,
      }));
      message.error("Lot을 추가할 수 없습니다");
    }
  };

  const handleLotDetail = (lot) => {
    if (!product) return;
    history.push(`/ldas/detail/${product.id}/lot/${lot.id}`);
  };

  const handleLotEdit = (id) => {
    setEditingLotId(id);
    setTimeout(() => {
      inputRefs.current[id]?.focus();
      inputRefs.current[id]?.select();
    }, 0);
  };

  const handleLotNumberChange = (id, value) => {
    setLotListPage((prev) => ({
      ...prev,
      content: prev.content.map((l) =>
        l.id === id ? { ...l, lotNumber: value } : l,
      ),
    }));
  };

  const handleLotNumberBlur = async (id) => {
    const lot = lotListPage.content.find((l) => l.id === id);
    const trimmed = lot?.lotNumber?.trim();
    setLotListPage((prev) => ({
      ...prev,
      content: prev.content.map((l) => {
        if (l.id !== id) return l;
        return { ...l, lotNumber: trimmed || l.lotNumber };
      }),
    }));
    setEditingLotId((cur) => (cur === id ? null : cur));

    if (!trimmed) return;
    try {
      const res = await LdasApi.updateLdasLot(id, { lotNumber: trimmed });
      if (res?.code !== 200) {
        message.error(res?.message ?? "Lot을 수정할 수 없습니다");
        await refetchLotsOnly(
          lotListPage.pageable.pageNumber,
          lotListPage.pageable.pageSize,
        );
      }
    } catch (e) {
      console.error("updateLdasLot", e);
      message.error("Lot을 수정할 수 없습니다");
      await refetchLotsOnly(
        lotListPage.pageable.pageNumber,
        lotListPage.pageable.pageSize,
      );
    }
  };

  const handleLotDelete = (lot) => {
    Swal.fire({
      ...LDAS_DELETE_SWAL_CONFIG,
      title: `"${lot.lotNumber}" 로트를 삭제할까요?`,
      text: "정말로 이 로트를 삭제하시겠습니까?",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        const res = await LdasApi.deleteLdasLot(lot.id);
        if (res?.code !== 200) {
          message.error(res?.message ?? "Lot을 삭제할 수 없습니다");
          return;
        }
        await refetchLotsOnly(
          lotListPage.pageable.pageNumber,
          lotListPage.pageable.pageSize,
        );
      } catch (e) {
        console.error("deleteLdasLot", e);
        message.error("Lot을 삭제할 수 없습니다");
      }
    });
  };

  const handleEdit = () => {
    history.push(`/ldas/edit/${productId}`);
  };

  const handleDelete = () => {
    void deleteLdasProductWithConfirm({
      productId,
      productName: product?.productName,
      imageKey: product?.imageUrl,
    });
  };

  const handleLotTablePaginationChange = (nextPage, nextPageSize) => {
    setLotListPage((prev) => {
      const prevSize = prev.pageable.pageSize;
      const size = nextPageSize ?? prevSize;
      const sizeChanged = size !== prevSize;
      return {
        ...prev,
        pageable: {
          pageNumber: sizeChanged ? 0 : nextPage - 1,
          pageSize: size,
        },
      };
    });
  };

  const columns = useMemo(
    () =>
      getLotColumns({
        editingLotId,
        inputRefs,
        onNumberChange: handleLotNumberChange,
        onNumberBlur: handleLotNumberBlur,
        onDetail: handleLotDetail,
        onEdit: handleLotEdit,
        onDelete: handleLotDelete,
      }),
    [editingLotId, lotListPage.content, product?.id],
  );

  if (loading) {
    return (
      <div className="ldas-page__content ldas-page__content--detail-loading">
        <Spin size="large" tip="불러오는 중..." />
      </div>
    );
  }

  if (!product) {
    return null;
  }

  return (
    <div className="ldas-page__content">
      <div className="ldas-detail-breadcrumb">
        <span className="ldas-detail-breadcrumb__prev">제품 관리</span>
        <span className="ldas-detail-breadcrumb__sep">›</span>
        <span className="ldas-detail-breadcrumb__cur">
          {product.productName}
        </span>
      </div>

      <div className="ldas-detail-header">
        <div className="ldas-detail-header__title-wrap">
          <h1 className="ldas-detail-header__title">{product.productName}</h1>
          <p className="ldas-detail-header__brand">{product.brandName}</p>
        </div>
        <div className="ldas-detail-header__actions">
          <button
            type="button"
            className="ldas-detail-btn ldas-detail-btn--edit"
            onClick={handleEdit}
          >
            수정
          </button>
          <button
            type="button"
            className="ldas-detail-btn ldas-detail-btn--delete"
            onClick={handleDelete}
          >
            삭제
          </button>
        </div>
      </div>

      <div className="ldas-detail-layout">
        <LdasDetailProductCard product={product} />

        <LdasDetailLotTable
          lots={lotListPage.content}
          lotCount={lotListPage.totalElements}
          columns={columns}
          onLotAdd={handleLotAdd}
          // pagination={{
          //   current: lotListPage.pageable.pageNumber + 1,
          //   pageSize: lotListPage.pageable.pageSize,
          //   total: lotListPage.totalElements,
          //   hideOnSinglePage:
          //     lotListPage.totalElements <= lotListPage.pageable.pageSize,
          //   onChange: handleLotTablePaginationChange,
          //   onShowSizeChange: handleLotTablePaginationChange,
          // }}
          pagination={{
            current: lotListPage.pageable.pageNumber + 1,
            pageSize: lotListPage.pageable.pageSize,
            total: lotListPage.totalElements,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} / 총 ${total}건`,
            hideOnSinglePage: false,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50"],
            onChange: handleLotTablePaginationChange,
            onShowSizeChange: handleLotTablePaginationChange,
          }}
        />
      </div>

      <LdasLotAddModal
        modalShell={LdasModal}
        visible={lotAddModalOpen}
        onCancel={handleLotAddModalCancel}
        onAdd={handleLotAddSubmit}
      />
    </div>
  );
};

export default LdasProductDetailComponent;
